import { useState } from 'react';
import { apiAdminService } from '@api';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useParams } from 'react-router-dom';
import { TAmlAlertLite, TAmlAlertListOptions } from '@xeppt/xeppt-sdk/types';

const ALERTS_LIMIT = 20;

interface IProps {
    isInitialAlerts?: boolean;
    isInitialAlert?: boolean;
}

export type TAmlAlertsFilter = {
    status?: TAmlAlertListOptions['status'];
    severity?: TAmlAlertListOptions['severity'];
    alertType?: TAmlAlertListOptions['alertType'];
    from?: string;
    to?: string;
    order: 'asc' | 'desc';
};

const DEFAULT_ALERTS_FILTER: TAmlAlertsFilter = {
    order: 'desc'
};

export const useAmlAlerts = ({ isInitialAlerts = false, isInitialAlert = false }: IProps) => {
    const [alertsOffset, setAlertsOffset] = useState(0);
    const [alerts, setAlerts] = useState<TAmlAlertLite[]>([]);
    const [alertsTotal, setAlertsTotal] = useState(0);
    const [alertsFilter, setAlertsFilter] = useState<TAmlAlertsFilter>(DEFAULT_ALERTS_FILTER);
    const { id } = useParams<{ id: string }>();

    const { data: alertsData, handleRequest: refetchAlerts } = useApiQuery({
        method: (request: any) => {
            const filter: TAmlAlertListOptions = request?.filter ?? alertsFilter;
            return apiAdminService.getAmlAlerts({
                limit: ALERTS_LIMIT,
                offset: request?.isNew ? 0 : alertsOffset,
                status: filter.status,
                severity: filter.severity,
                alertType: filter.alertType,
                from: filter.from,
                to: filter.to,
                order: filter.order
            });
        },
        onSuccess: (data, request?: any) => {
            if (request?.isNew) {
                setAlerts(data.items);
                setAlertsOffset(data.items.length);
            } else {
                setAlerts((state) => [...state, ...data.items]);
                setAlertsOffset((prev) => prev + data.items.length);
            }
            setAlertsTotal(data.total);
        },
        isInitialRequest: isInitialAlerts
    });

    const { data: alert } = useApiQuery({
        method: () => apiAdminService.getAmlAlertById(id || ''),
        condition: !!id,
        deps: [id],
        isInitialRequest: isInitialAlert
    });

    const handleChangeAlertsFilter = (data: Partial<TAmlAlertsFilter>) => {
        const next = { ...alertsFilter, ...data };
        setAlertsFilter(next);
        setAlerts([]);
        setAlertsOffset(0);
        refetchAlerts({ isNew: true, filter: next });
    };

    return {
        alerts,
        alert,
        alertsTotal,
        alertsData,
        alertsFilter,
        refetchAlerts,
        handleChangeAlertsFilter
    };
};
